import React, { useContext, useMemo, useState } from 'react';
import { DictionaryIndex } from 'lib';

import { IndexCrosswalk } from './index-list-crosswalk';

interface IndexListContextProps {
    initIndexList: (indexesToLoad: DictionaryIndex[]) => void;
    setIndex: (id: string, value: any) => void;
    addIndex: () => void;
    deleteIndex: (index: DictionaryIndex) => void;
    setSelectedIndex: (indexId?: string) => void;
    indexes: DictionaryIndex[];
    newAddedIndex: DictionaryIndex | undefined;
    setNewAddedIndex: React.Dispatch<
        React.SetStateAction<DictionaryIndex | undefined>
    >;
    crosswalk: any;
    selectedIndex: any;
}

const IndexListContext = React.createContext<IndexListContextProps>({
    initIndexList: () => {},
    setIndex: () => {},
    setSelectedIndex: () => {},
    addIndex: () => {},
    deleteIndex: () => {},
    indexes: [],
    newAddedIndex: undefined,
    setNewAddedIndex: () => {},
    crosswalk: {},
    selectedIndex: {},
});

const useIndexList: () => IndexListContextProps = () =>
    useContext(IndexListContext);

const IndexListContextProvider = ({ children }: any): React.JSX.Element => {
    const [crosswalk, setCrosswalk] = useState<IndexCrosswalk>();
    const [indexList, setIndexList] = useState<DictionaryIndex[]>([]);
    const [selectedIdx, setSelectedIdx] = useState<DictionaryIndex>();
    const [newAddedIndex, setNewAddedIndex] = useState<DictionaryIndex>();

    const init = (indexesToLoad: DictionaryIndex[]): void => {
        const cw = new IndexCrosswalk(indexesToLoad || []);
        setCrosswalk(cw);
        setIndexList(indexesToLoad);
    };

    const setIndex = (id: string, newValue: DictionaryIndex): void => {
        crosswalk?.setItem(id, newValue);
    };

    const handleSelectIndex = (indexId?: string): void => {
        if (indexId) {
            setSelectedIdx(crosswalk?.getItem(indexId));
        } else {
            setSelectedIdx(undefined);
        }
    };

    const handleDeleteIndex = (index: any): void => {
        let newIndexList: DictionaryIndex[] = [];

        indexList.map((i: DictionaryIndex) => {
            if (i.id !== index.id) {
                newIndexList.push(i);
            }

            return i;
        });

        setIndexList(newIndexList);

        crosswalk?.crosswalk.delete(index.id);
    };

    const handleAddIndex = (): void => {
        const newIdx: DictionaryIndex = {
            id: crypto.randomUUID(),
            name: 'newIndex',
            type: 'NORMAL',
        };

        let newIndexList: DictionaryIndex[] = indexList.map(
            (i: DictionaryIndex) => {
                return i;
            }
        );
        newIndexList.push(newIdx);
        if (newIndexList.length > 0) {
            const newIndexData = newIndexList.length - 1;
            setNewAddedIndex(newIndexList[newIndexData]);
        } else {
            setNewAddedIndex(newIndexList[0]);
        }
        setIndexList(newIndexList);

        crosswalk?.crosswalk.set(newIdx.id, newIdx);
    };

    return (
        <IndexListContext.Provider
            value={useMemo(
                () => ({
                    initIndexList: (indexesToLoad: DictionaryIndex[]) =>
                        init(indexesToLoad),
                    setIndex: (id: string, newValue: DictionaryIndex) =>
                        setIndex(id, newValue),
                    setSelectedIndex: (indexId?: string) =>
                        handleSelectIndex(indexId),
                    addIndex: () => handleAddIndex(),
                    deleteIndex: (index: any) => handleDeleteIndex(index),
                    indexes: indexList,
                    newAddedIndex: newAddedIndex,
                    setNewAddedIndex: setNewAddedIndex,
                    selectedIndex: selectedIdx,
                    crosswalk: crosswalk,
                }),
                [selectedIdx, indexList] // eslint-disable-line react-hooks/exhaustive-deps
            )}
        >
            {children}
        </IndexListContext.Provider>
    );
};

export { IndexListContextProvider, useIndexList };
